import { Button } from "./ui/button";
import { PageHero, HeroLink } from "./PageHero";
import { Mark } from "./Mark";
import { MapPin, Phone } from "lucide-react";
import { siteConfig } from "../config/siteConfig";

interface AboutPageProps {
  onNavigate: (page: string) => void;
  onNavigateToDemo: () => void;
}

// Copy from the Ariviti company pages (ariviti_website_docs/copy/23-about.md, 24-purpose-values.md,
// 01-index.md). Client superlatives and the Forbes Global 2000 count are held back there pending
// client-naming permission, so they are not used here.
const proof = [
  { metric: "20+", label: "Years building and running production systems" },
  { metric: "90%+", label: "Repeat business — the number we watch most closely" },
  { metric: "5", label: "Practices, from product engineering to TurfAI" },
  { metric: "8", label: "Industry domains we serve" },
];

const values = [
  { name: "Move fast", body: "We choose speed over certainty — deciding quickly and moving faster, because hesitation costs more than a wrong turn." },
  { name: "Bet bold", body: "We choose the harder, higher-upside path — in what we plan, how we build, and every call in between." },
  { name: "Customer first", body: "Their success is the measure of ours — we build for their outcome, not our output." },
  { name: "Robust delivery", body: "What we build, run, or deliver is proven at real-world scale." },
  { name: "Amplify judgment", body: "We use AI to amplify and accelerate human judgment — starting with our own." },
];

const H2 = "text-[32px] lg:text-[40px] leading-tight font-bold tracking-tight text-dark-indigo";

export function AboutPage({ onNavigateToDemo }: AboutPageProps) {
  return (
    <div className="min-h-screen bg-white">
      <PageHero
        title={<>TurfAI is built by Ariviti. <Mark>Intelligence Amplified.</Mark></>}
        lede="Two decades of building and running the systems institutions depend on — banks, telcos, governments, universities — now with our own AI platform at the centre of how we deliver."
        actions={
          <>
            <Button variant="cta" size="cta" onClick={onNavigateToDemo}>Schedule a demo</Button>
            <HeroLink onClick={() => (window.location.href = "https://ariviti.com")}>Visit ariviti.com</HeroLink>
          </>
        }
      />

      {/* What we are */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto grid gap-12 px-4 lg:grid-cols-[1fr_1.4fr]">
          <h2 className={H2}>What <Mark>we are</Mark></h2>
          <div className="space-y-5 text-[18px] leading-relaxed text-soft-black">
            <p>We build and run the systems institutions depend on — banks, telcos, governments, universities and the platforms that carry them.</p>
            <p>For over two decades we have worked with global brands as a digital engineering partner: card issuance systems and remittance corridors, 5G network functions and value-added services platforms, national-scale robotic process automation, university learning platforms, and observability estates in capital markets and manufacturing.</p>
            <p>The common thread is not a technology. In every one of those environments, a system going wrong is somebody's serious problem — a regulator's, a customer's, a citizen's. That is the kind of work we are set up for, and it is why TurfAI is built the way it is: governed, auditable, with a person approving the steps that matter.</p>
          </div>
        </div>
      </section>

      {/* Proof bar */}
      <section className="border-y border-softblack-tint-20 band-surface">
        <div className="container mx-auto grid grid-cols-2 gap-8 px-4 py-14 lg:grid-cols-4">
          {proof.map((p) => (
            <div key={p.label}>
              <div className="font-display text-[44px] lg:text-[52px] font-bold leading-none text-dark-indigo">{p.metric}</div>
              <p className="mt-3 text-[15px] text-soft-black">{p.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Two histories */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <h2 className={H2}>Two histories, <Mark>one company</Mark></h2>
          <p className="mt-4 max-w-3xl text-[19px] leading-relaxed text-soft-black">
            Ariviti brings together Integra Micro Software Services and OpenTurf Technologies. Same engineers, same clients, one operating model — and a name that says what the combined company is for.
          </p>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            <div className="rounded-lg border border-softblack-tint-20 p-7">
              <h3 className="font-display text-[22px] font-semibold text-dark-indigo">Integra Micro Software Services</h3>
              <p className="mt-3 text-[16px] leading-relaxed text-soft-black">
                Two decades of enterprise delivery for large institutions: infrastructure modernisation, IT operations, observability and SecOps, quality engineering under the i3QA™ practice, robotic process automation at national scale, and capability-centre enablement. In August 2026, IMSS became Ariviti Technologies.
              </p>
            </div>
            <div className="rounded-lg border border-softblack-tint-20 p-7">
              <h3 className="font-display text-[22px] font-semibold text-dark-indigo">OpenTurf Technologies</h3>
              <p className="mt-3 text-[16px] leading-relaxed text-soft-black">
                A custom software and system integration firm built around the Virtual CTO idea. High-availability telecom and banking-grade platforms, payment corridors clearing over $1bn a month, 5G network functions and OTT platforms — and the team that built TurfAI.
              </p>
            </div>
          </div>
          <div className="mt-10 max-w-3xl">
            <h3 className="font-display text-[22px] font-semibold text-dark-indigo">Why one company</h3>
            <p className="mt-3 text-[17px] leading-relaxed text-soft-black">
              The two firms were solving the same problem from opposite ends. One was strong on running large enterprise estates; the other on building new products fast. Together they cover the whole arc: create the system, manage it in production, and verify that it still does what it was built to do.
            </p>
          </div>
        </div>
      </section>

      {/* The name */}
      <section className="py-20 lg:py-24 bg-dark-indigo text-white">
        <div className="container mx-auto grid gap-10 px-4 lg:grid-cols-[1fr_1.4fr]">
          <div>
            <p className="text-sm font-semibold text-indigo-tint-20">The name</p>
            <h2 className="mt-3 text-[40px] lg:text-[52px] font-bold leading-none tracking-tight">Ariviti</h2>
            <p className="mt-3 font-display text-[22px] text-indigo-tint-20">Intelligence Amplified.</p>
          </div>
          <div className="space-y-5 text-[18px] leading-relaxed text-indigo-tint-20">
            <p>We exist to amplify the intelligence inside the institutions the world runs on — deep domain expertise, sharpened by applied AI, to create and manage systems that people can trust.</p>
            <p>The governing idea: the future of AI is guided by human wisdom. That is a claim, so we are obliged to prove it — by using AI where it makes a judgement sharper, and declining to use it where it only makes a pitch louder. TurfAI's human-in-the-loop design is that idea in software.</p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 lg:py-24 band-surface">
        <div className="container mx-auto px-4">
          <h2 className={H2}>Five decisions we have <Mark>already made</Mark></h2>
          <p className="mt-4 max-w-3xl text-[19px] leading-relaxed text-soft-black">
            Not aspirations — the tie-breakers we use when two reasonable people disagree.
          </p>
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-5">
            {values.map((v) => (
              <div key={v.name} className="rounded-lg border border-softblack-tint-20 bg-white p-6">
                <h3 className="font-display text-[19px] font-semibold text-dark-indigo">{v.name}.</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-soft-black">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications + where we are */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto grid gap-12 px-4 lg:grid-cols-2">
          <div>
            <h2 className={H2}>Certified to <Mark>deliver</Mark></h2>
            <div className="mt-6 flex flex-wrap gap-2">
              {["ISO 27001", "ISO 9001", "CMMI Level 3", "HIPAA", "GDPR", "CASA"].map((c) => (
                <span key={c} className="rounded-full border border-softblack-tint-20 px-4 py-1.5 text-[14px] font-semibold text-dark-indigo">{c}</span>
              ))}
            </div>
            <p className="mt-6 max-w-xl text-[16px] text-soft-black">Certificates and audit reports are shared during security review.</p>
          </div>
          <div>
            <h2 className={H2}>Where <Mark>we are</Mark></h2>
            <p className="mt-6 flex items-start gap-3 text-[16px] leading-relaxed text-soft-black">
              <MapPin className="mt-1 h-5 w-5 flex-shrink-0 text-royal-indigo" />
              <span>10th floor, RMZ Latitude Commercial Building, Bellary Road, Hebbal, Bengaluru, Karnataka 560024 — our primary delivery centre.</span>
            </p>
            <p className="mt-3 flex items-center gap-3 text-[16px] text-soft-black">
              <Phone className="h-5 w-5 flex-shrink-0 text-royal-indigo" />
              <a href={siteConfig.contact.phoneHref} className="font-semibold text-royal-indigo hover:text-dark-indigo">{siteConfig.contact.phone}</a>
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-dark-indigo text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-[36px] lg:text-[42px] leading-tight font-bold tracking-tight mb-6">Want to know whether we are <Mark>right for this</Mark>?</h2>
          <p className="text-[22px] text-indigo-tint-20 mb-8 max-w-2xl mx-auto">
            Ask us directly. We turn down work that does not fit, and we would rather do it in the first conversation than the fourth.
          </p>
          <Button variant="onDark" size="lg" className="px-8 h-12" onClick={onNavigateToDemo}>Talk to us</Button>
        </div>
      </section>
    </div>
  );
}
